import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../../firebase";
import { getDoc, doc } from "firebase/firestore";
import { supabase } from "../../supabase";
import { useAppShell } from "../AppShellContext";
import QuickNote from "../../components/QuickNote";
import FocusRoom from "../../components/FocusRoom";
import DailyChallenge from "../../components/Dailychallenge"; // ✅ Added import for the challenge component

const QUOTES = [
  "Small steps every day add up to miles over time.",
  "You don't have to be positive all the time. You just have to keep going.",
  "Healing is not linear, and that's okay.",
  "Be kind to yourself. You're doing better than you think.",
  "The only way out is through — and you're already moving.",
  "Rest is not giving up. It's gearing up.",
  "Your feelings are valid. Every single one.",
  "Progress is still progress, no matter how small.",
  "You are allowed to take up space and ask for help.",
  "Today is a fresh page. Write something kind on it.",
  "Breathe in courage, breathe out fear.",
  "You've survived 100% of your worst days so far.",
  "Gentle with yourself — you're growing through this.",
  "One good thought can shift your entire day.",
];
const getDailyQuote = () => QUOTES[Math.floor(Date.now() / 86400000) % QUOTES.length];

function getGreeting() {
  const h = new Date().getHours();
  if (h < 5)  return { text: "good night",     icon: "🌙" };
  if (h < 12) return { text: "good morning",   icon: "🌅" };
  if (h < 17) return { text: "good afternoon",  icon: "☀️" };
  if (h < 21) return { text: "good evening",   icon: "🌆" };
  return { text: "good night", icon: "🌙" };
}

const MOODS = [
  { id: "great",   emoji: "😊", label: "Great",   score: 90, tip: "Amazing! Keep that energy going 🌟" },
  { id: "okay",    emoji: "😐", label: "Okay",    score: 60, tip: "Steady day — that counts too 💛" },
  { id: "low",     emoji: "😔", label: "Low",     score: 30, tip: "Feeling low? Saksham is here 💚" },
  { id: "anxious", emoji: "😤", label: "Anxious", score: 25, tip: "Try a breathing exercise to calm down 🌬️" },
  { id: "tired",   emoji: "😴", label: "Tired",   score: 40, tip: "Rest is productive too. Be gentle 🛌" },
];

const FOCUS_ITEMS = [
  { id: "saksham",   emoji: "🤖", label: "Saksham",   tab: "saksham" },
  { id: "challenge", emoji: "🎯", label: "Challenge", modal: "challenge" }, // ✅ Added Challenge Button
  { id: "exercises", emoji: "💪", label: "Exercises", tab: "explore", chip: "exercises" },
  { id: "journal",   emoji: "📓", label: "Journal",   route: "/journal" },
  { id: "soulscape", emoji: "🌌", label: "SoulScape", modal: "focusroom" }, 
  { id: "games",     emoji: "🎮", label: "Games",     tab: "explore", chip: "games" },
];

const TILES = [
  { id: "insight",  emoji: "📊", name: "Insight",      desc: "AI mood report",  route: "/journal", color: "brand" },
  { id: "appt",     emoji: "📅", name: "Book Session", desc: "Talk to a mentor", tab: "connect", connectTab: "mentors", color: "sage" },
  { id: "feed",     emoji: "📰", name: "Community",    desc: "Share & support",  tab: "connect", connectTab: "feed",    color: "peach" },
  { id: "assess",   emoji: "🧪", name: "Assessment",   desc: "Know yourself",    tab: "explore", chip: "assess",        color: "cloud" },
];

export default function HomeScreen() {
  const navigate = useNavigate();
  
  const { user, setActiveTab, setExploreChip, setConnectTab, resetTrigger, activeTab } = useAppShell(); 
  
  const [firstName, setFirstName] = useState("");
  const [streak, setStreak] = useState(0);
  const [selectedMood, setSelectedMood] = useState(null);
  const [moodTip, setMoodTip] = useState("");
  const [moodSaving, setMoodSaving] = useState(false);
  const [showFocusRoom, setShowFocusRoom] = useState(false);
  const [showChallenge, setShowChallenge] = useState(false); // ✅ Added state for the challenge modal
  
  const greeting = getGreeting();
  const today = new Date();
  const dateStr = today.toLocaleDateString("en-US", { weekday: "short", day: "numeric", month: "long" });

  // The Reset Listener. Closes SoulScape and Challenge when the home icon is double-clicked
  useEffect(() => {
    if (activeTab === "home") {
      setShowFocusRoom(false);
      setShowChallenge(false); // ✅ Close challenge modal on home double-tap
    }
  }, [resetTrigger, activeTab]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        const d = snap.data();
        const fn = d?.basicInfo?.firstName || d?.profile?.firstName || user?.displayName?.split(" ")[0] || "";
        setFirstName(fn);
      } catch { /* silent */ }
    })();

    const todayStr = new Date().toDateString();
    const lastCheck = localStorage.getItem("lastCheckInDate");
    let s = parseInt(localStorage.getItem("streakCount")) || 0;
    if (lastCheck !== todayStr) {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      if (lastCheck === yesterday.toDateString()) { s++; }
      else if (!lastCheck) { s = 1; }
      else { s = 1; }
      localStorage.setItem("streakCount", s);
      localStorage.setItem("lastCheckInDate", todayStr);
    }
    setStreak(s);
  }, [user]);

  const handleMoodSelect = useCallback(async (mood) => {
    setSelectedMood(mood.id);
    setMoodTip(mood.tip);
    localStorage.setItem("hh_mood", mood.score);
    if (!user) return;
    setMoodSaving(true);
    try {
      await supabase.from("mood_logs").insert([{
        user_id: user.uid, score: mood.score, short_thought: "",
        created_at: new Date().toISOString(),
      }]);
      const { count } = await supabase.from("mood_logs")
        .select("id", { count: "exact", head: true }).eq("user_id", user.uid);
      if (count > 50) {
        const excess = count - 50;
        const { data: oldest } = await supabase.from("mood_logs").select("id")
          .eq("user_id", user.uid).order("created_at", { ascending: true }).limit(excess);
        if (oldest?.length) {
          await supabase.from("mood_logs").delete().in("id", oldest.map(l => l.id));
        }
      }
    } catch { /* silent */ }
    finally { setMoodSaving(false); }
  }, [user]);

  const handleFocusTap = useCallback((item) => {
    if (item.modal === "focusroom") {
      setShowFocusRoom(true);
    } else if (item.modal === "challenge") {
      setShowChallenge(true); // ✅ Open Challenge modal on tap
    } else if (item.tab) {
      if (item.chip && setExploreChip) {
        setExploreChip(item.chip);
      }
      setActiveTab(item.tab);
    } else if (item.route) {
      navigate(item.route);
    }
  }, [setActiveTab, setExploreChip, navigate]);

  const handleTileTap = useCallback((tile) => {
    if (tile.connectTab && setConnectTab) {
      setConnectTab(tile.connectTab);
    }
    if (tile.chip && setExploreChip) {
      setExploreChip(tile.chip);
    }
    
    if (tile.tab) setActiveTab(tile.tab);
    else if (tile.route) navigate(tile.route);
  }, [setActiveTab, setExploreChip, setConnectTab, navigate]);

  return (
    <>
      <div className="hh-home">
        {/* GREETING ZONE */}
        <div className="hh-greet">
          <div className="hh-greet-time">{greeting.icon} {greeting.text} · {dateStr}</div>
          <div className="hh-greet-name">Hey, {firstName || "there"} 👋</div>
          {streak > 0 && <div className="hh-streak-pill">🔥 {streak} day streak!</div>}
          <div className="hh-greet-quote">"{getDailyQuote()}"</div>
        </div>

        {/* MOOD TRACKER */}
        <div className="hh-mood-section">
          <div className="hh-section-label">how are you feeling?</div>
          <div className="hh-mood-row">
            {MOODS.map(m => (
              <button key={m.id} className={`hh-mood-btn${selectedMood === m.id ? " on" : ""}`}
                onClick={() => handleMoodSelect(m)} disabled={moodSaving} aria-label={m.label}>
                <span className="hh-mood-emoji">{m.emoji}</span>
                <span className="hh-mood-label">{m.label}</span>
              </button>
            ))}
          </div>
          {moodTip && <div className="hh-mood-tip">{moodTip}</div>}
        </div>

        {/* TODAY'S FOCUS */}
        <div className="hh-focus-section">
          <div className="hh-section-label" style={{ padding: "0 18px" }}>today's focus</div>
          <div className="hh-focus-scroll">
            {FOCUS_ITEMS.map((item, i) => (
              <button key={item.id} className={`hh-focus-chip${i === 0 ? " primary" : ""}`}
                onClick={() => handleFocusTap(item)}>
                {item.emoji} {item.label}
              </button>
            ))}
          </div>
        </div>

        {/* QUICK NOTE */}
        <div className="hh-home-card-wrap">
          <div className="hh-section-label">⚡ quick check-in</div>
          <div className="hh-card hh-quicknote-wrap">
            <QuickNote />
          </div>
        </div>

        {/* SOULSCAPE CARD */}
        <div className="hh-home-card-wrap">
          <div className="hh-soulscape-card" onClick={() => setShowFocusRoom(true)} role="button" tabIndex={0}>
            <div className="hh-sc-badge">✨ Immersive</div>
            <div className="hh-sc-title">SoulScape 🌌</div>
            <div className="hh-sc-desc">Your personal focus sanctuary. Ambient scenes + sound + focus timer.</div>
            <span className="hh-sc-cta">Enter SoulScape →</span>
          </div>
        </div>

        {/* QUICK ACCESS GRID */}
        <div className="hh-home-card-wrap">
          <div className="hh-section-label">quick access</div>
          <div className="hh-qtile-grid">
            {TILES.map(tile => (
              <div key={tile.id} className={`hh-qtile hh-qtile-${tile.color}`}
                onClick={() => handleTileTap(tile)} role="button" tabIndex={0}>
                <span className="hh-qtile-icon">{tile.emoji}</span>
                <div className="hh-qtile-name">{tile.name}</div>
                <div className="hh-qtile-desc">{tile.desc}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ height: 20 }} />
      </div>
      
      {/* ── FOCUS ROOM MODAL ── */}
      {showFocusRoom && (
        <div className="hh-focusroom-overlay" style={{
          position: "fixed", inset: 0, zIndex: 99999,
          background: "#000", display: "flex", flexDirection: "column"
        }}>
          <FocusRoom 
            isEmbeddedFullscreen={true} 
            onClose={() => setShowFocusRoom(false)} 
          />
        </div>
      )}

      {/* ── ✅ DAILY CHALLENGE MODAL ── */}
      {showChallenge && (
        <div className="hh-challenge-overlay" style={{
          position: "fixed", inset: 0, zIndex: 99999,
          background: "var(--bg-color, #fff)", 
          display: "flex", flexDirection: "column",
          overflowY: "auto", padding: "20px"
        }}>
          {/* Header for the Modal */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
            <h2 style={{ margin: 0, fontFamily: "'DM Serif Display', serif", fontSize: "1.6rem", color: "var(--text-color, #111)" }}>
              Daily Challenge
            </h2>
            <button 
              onClick={() => setShowChallenge(false)}
              style={{ 
                background: "var(--bg-secondary, #f3f4f6)", 
                color: "var(--text-color, #111)", 
                border: "none", 
                borderRadius: "50%", 
                width: 36, 
                height: 36, 
                fontWeight: "bold", 
                cursor: "pointer", 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center" 
              }}
              aria-label="Close Daily Challenge"
            >
              ✕
            </button>
          </div>
          
          <DailyChallenge />
        </div>
      )}
    </>
  );
}